import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Confirm, Icons, Inform, Modal } from '@components/common';
import PageTitle from '@components/common/PageTitle';
import Table from '@components/common/Table';
import ImportMapDataContainer from '@components/ModalComponents/ImportMapDataContainer';
import ManageSiteForm from '@components/ModalComponents/ManageSiteForm';

import { useLinkAndVerifySites } from '@hooks/useLinkAndVerifySite';
import { useManageSites } from '@hooks/useManageSites';
import { useManageSitesOptions } from '@hooks/useManageSitesOptions';

import { auth, sites as sitesSelector } from '@store/selectors';
import { useAppDispatch, useAppSelector } from '@store/store';

import {
	setInstalledOnAsset,
	setPage,
	setPageSize,
	setSearchValue,
	setSiteType,
	setSort,
	setTechnology,
	setVendor,
} from '@slices/sites';

import { isEmpty } from '@utils/common';
import { setFilterUtil } from '@utils/filterUtil';

import { IManageSiteValuesDTO } from '@interfaces/cells/dto/cellsDTO';
import { IGroupOperationWithActionDTO } from '@interfaces/common/defaultDTO';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { ISortBy, ITablePagination, ITableSearch, ITableSort, TTableFilter } from '@interfaces/components/table';
import { InRangeStatuses } from './interfaces';

import { EAdminPanelPageTitles, TMenuActionsTypes } from '@enums/common';
import { EImportingDataType } from '@enums/dropzone';

import { MODAL_WIDTH_580 } from '@constants/modals';
import {
	addTooltipText,
	installedOnAssetOptions,
	profilePath,
	searchTooltipText,
	siteCantBeLinkedWarning,
	siteOutOfRadiusText,
	updateCoordsAndLinkText,
	updateCoordsText,
} from './constants';
import {
	acceptedSitesFileTypes,
} from '@components/ModalComponents/ImportMapDataContainer/ImportMapData/UploadFile/constants';

import LinkSiteToAssetForm from './LinkSiteToAssetForm';
import { getColumns } from './manageSitesColumns';
import SitesImportForm from './SitesImportForm';

import styles from './ManageSites.module.scss';

const ManageSites = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();

	const [isDirtyFormModal, setIsDirtyFormModal] = useState(false);
	const [isSiteFormModal, setIsSiteFormModal] = useState(false);
	const [isDeleteConfirm, setIsDeleteConfirm] = useState(false);
	const [isUnlinkConfirm, setIsUnlinkConfirm] = useState(false);
	const [isLinkFormModal, setIsLinkFormModal] = useState(false);
	const [isInRangeInform, setIsInRangeInform] = useState(false);
	const [isRewriteCoordConfirm, setIsRewriteCoordConfirm] = useState(false);
	const [isSingleSiteVerified, setIsSingleSiteVerified] = useState(false);
	const [isImportSites, setIsImportSites] = useState(false);
	const [isImportFormModal, setIsImportFormModal] = useState(false);

	const [rangeStatuses, setRangeStatuses] = React.useState<InRangeStatuses | null>(null);
	const [selectedInstalledOnAsset, setSelectedInstalledOnAsset] = useState<TSelectOption | null>(null);
	const [selectedVendor, setSelectedVendor] = useState<TSelectOption | null>(null);
	const [selectedVendorImport, setSelectedVendorImport] = useState<TSelectOption<string> | null>(null);
	const [selectedSiteType, setSelectedSiteType] = useState<TSelectOption | null>(null);
	const [selectedTechnology, setSelectedTechnology] = useState<TSelectOption | null>(null);
	const [checkedIds, setCheckedIds] = useState<Array<string>>([]);
	const [actionId, setActionId] = useState<TMenuActionsTypes>();
	const [singleSelectedSiteId, setSingleSelectedSiteId] = useState<string | null>(null);
	const [assetDbId, setAssetDbId] = useState<string | null>(null);
	const [editableSite, setEditableSite] = useState<Record<string, any> | null>(null);
	const [fieldsTechnology, setFieldsTechnology] = useState<string | undefined>();

	const {
		accessPermission: {
			networkOperatorTelecomSitesAttributeUpdater,
			assetOwnerOrganizationAdministrator,
			assetOwnerViewer,
			assetOwnerContact,
			assetOwnerDefaultContact,
			assetOwnerAttributeUpdater,
			assetOwnerSignatory,
			networkOperatorAssetEnquirer,
			networkOperatorTelecomSitesViewer,
			assetConsumerAssetEnquirer,
		},
	} = useAppSelector(auth);
	const isEditAccess = networkOperatorTelecomSitesAttributeUpdater;
	const isManageAssetAccess = assetOwnerOrganizationAdministrator || assetOwnerViewer || assetOwnerContact
		|| assetOwnerDefaultContact || assetOwnerAttributeUpdater || assetOwnerSignatory;
	const isLinkAccess = networkOperatorTelecomSitesAttributeUpdater
		&& (networkOperatorAssetEnquirer || assetConsumerAssetEnquirer);
	const isInstalledOnAssetColumnShown = networkOperatorTelecomSitesAttributeUpdater
		&& (networkOperatorAssetEnquirer || assetConsumerAssetEnquirer);

	const toggleSiteModal = () => setIsSiteFormModal(prevState => !prevState);
	const closeSiteModal = () => setIsSiteFormModal(false);

	const openImportFormModal = () => setIsImportFormModal(true);
	const closeImportFormModal = () => setIsImportFormModal(false);

	const toggleLinkModal = () => setIsLinkFormModal(prevState => !prevState);

	const closeLinkModal = () => {
		setIsLinkFormModal(false);
		setSingleSelectedSiteId(null);
	};

	const toggleUnlinkConfirm = () => setIsUnlinkConfirm(prevState => !prevState);
	const closeUnlinkConfirm = () => {
		setIsUnlinkConfirm(false);
		setSingleSelectedSiteId(null);
	};

	const closeUpdateSiteModal = () => {
		setIsRewriteCoordConfirm(false);
		setRangeStatuses(null);
	};

	const toggleUpdateSiteModal = () => setIsRewriteCoordConfirm(prevState => !prevState);

	const toggleInRangeInform = () => setIsInRangeInform(prevState => !prevState);
	const closeInRangeInform = () => {
		setIsInRangeInform(false);
		setRangeStatuses(null);
	};

	const openAddSiteModal = () => {
		setEditableSite(null);
		toggleSiteModal();
	};

	const openDeleteConfirm = () => setIsDeleteConfirm(prevState => !prevState);

	const handleCustomDelete = (data: IGroupOperationWithActionDTO) => {
		openDeleteConfirm();
		setCheckedIds(data.ids);
		setActionId(data.actionType);
	};

	const closeDeleteConfirm = () => {
		setIsDeleteConfirm(false);
		setSingleSelectedSiteId(null);
	};

	const {
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		siteId,
		searchValue,
		vendor,
		siteType,
		technology,
		installedOnAsset,
	} = useAppSelector(sitesSelector);

	const {
		sitesDB,
		countOfRecords,
		isLoadingSites,
		isLoadingForm,
		generateSiteId,
		createSiteHandler,
		handleDeleteSite,
		handleDeleteSites,
		editSiteHandler,
		isLoadingDeleteSites,
	} = useManageSites({
		closeSiteModal,
		closeDeleteConfirm,
	});

	const {
		linkSiteHandler,
		unlinkSiteHandler,
		unlinkSitesHandler,
		updateAndLinkSiteHandler,
		updateLinkedSitesHandler,
		checkLinkedSitesHandler,
		inRadiusStatuses,
		inRadiusStatusesLoading,
	} = useLinkAndVerifySites({
		closeUnlinkConfirm,
		closeLinkModal,
		closeUpdateSiteModal,
		toggleUpdateSiteModal,
		toggleInRangeInform,
		isLinkAccess,
	});

	const {
		vendorsOptions,
		siteTypesOptions,
		technologiesOptions,
	} = useManageSitesOptions();

	const openProfileHandler = (id: string) => navigate(`${profilePath}/${id}`);

	const columns = getColumns({
		openDeleteConfirm,
		setSingleSelectedSiteId,
		isEditAccess,
		toggleSiteModal,
		setEditableSite,
		isLinkAccess,
		isInstalledOnAssetColumnShown,
		isManageAssetAccess,
		navigate,
		openProfileHandler,
		toggleLinkModal,
		toggleUnlinkConfirm,
		verifyCoordHandler: checkLinkedSitesHandler,
	});

	const submitHandler = (values: IManageSiteValuesDTO) => {
		if (editableSite) editSiteHandler({ values, id: editableSite.id });
		else createSiteHandler(values);
	};

	const linkHandler = (values: string) => {
		setAssetDbId(values);

		if (singleSelectedSiteId) linkSiteHandler({ assetDbId: values, siteDbId: singleSelectedSiteId });
	};

	const rewriteHandler = () => {
		if (rangeStatuses?.outOfRadius) updateLinkedSitesHandler(rangeStatuses.outOfRadius);
		else if (singleSelectedSiteId && assetDbId) {
			updateAndLinkSiteHandler({ assetDbId, siteDbId: singleSelectedSiteId });
		}
	};

	const handleUnlinkItems = () => {
		if (singleSelectedSiteId) return unlinkSiteHandler(singleSelectedSiteId);

		return unlinkSitesHandler(checkedIds);
	};

	const handleDeleteItems = () => {
		if (singleSelectedSiteId) return handleDeleteSite(singleSelectedSiteId);

		return handleDeleteSites({ ids: checkedIds, actionType: actionId });
	};

	const verifyCoordinates = (siteIds: string[]) => {
		checkLinkedSitesHandler(siteIds);
	};

	const pageHandler = (page: number) => dispatch(setPage({ page }));

	const pageSizeHandler = (pageSize: number) => dispatch(setPageSize({ pageSize }));

	const pagination: ITablePagination = {
		countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	};

	const onSearchHandler = (searchValue: string) => dispatch(setSearchValue({ searchValue }));

	const search: Array<ITableSearch> = [
		{
			initialValue: searchValue,
			name: 'siteId',
			handler: onSearchHandler,
			isNumber: true,
			tooltipText: searchTooltipText,
		},
	];

	const handleSort = (sortBy: Array<ISortBy>) =>
		dispatch(setSort({
			sortDirection: !!sortBy[0]?.desc,
			sortFieldName: sortBy[0]?.id,
		}));

	const sort: ITableSort = {
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	};

	const toggleImportSites = (actionId?: string) => {
		setFieldsTechnology(actionId);
		setIsImportSites(prevState => !prevState);
	};

	const toggleImport = ({
		vendor,
		technology,
	}: {
		vendor: TSelectOption<string>;
		technology: TSelectOption<string>;
	}) => {
		setSelectedVendorImport(vendor);
		setFieldsTechnology(technology?.value);
		closeImportFormModal();
		setIsImportSites(prevState => !prevState);
	};

	useEffect(() => {
		if (!isEmpty(inRadiusStatuses)) {
			const ids = inRadiusStatuses.reduce((acc: InRangeStatuses, item) => {
				item.isInRadius ? acc.inRadius.push(item.id) : acc.outOfRadius.push(item.id);

				return acc;
			}, { inRadius: [], outOfRadius: [] });

			setIsSingleSiteVerified(inRadiusStatuses.length === 1);
			setRangeStatuses(ids);
		}
	}, [inRadiusStatuses]);

	const filters: Array<TTableFilter> = [
		{
			name: 'vendor',
			placeholder: 'Vendor',
			option: selectedVendor,
			options: vendorsOptions,
			minWidth: '200px',
			setState: setSelectedVendor,
			defaultValue: vendor,
		},
		{
			name: 'siteType',
			placeholder: 'Site type/Civil model',
			option: selectedSiteType,
			options: siteTypesOptions,
			minWidth: '200px',
			setState: setSelectedSiteType,
			defaultValue: siteType,
		},
		{
			name: 'technology',
			placeholder: 'Technology',
			option: selectedTechnology,
			options: technologiesOptions,
			minWidth: '200px',
			setState: setSelectedTechnology,
			defaultValue: technology,
		},
		{
			name: 'installedOnAsset',
			placeholder: 'Installed on asset',
			option: selectedInstalledOnAsset,
			options: installedOnAssetOptions,
			minWidth: '200px',
			setState: setSelectedInstalledOnAsset,
			defaultValue: installedOnAsset,
		},
	];

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedVendor,
			currentValue: vendor,
			setAction: setVendor,
			fieldName: 'vendor',
			dispatch,
		});
	}, [dispatch, selectedVendor, vendor]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedSiteType,
			currentValue: siteType,
			setAction: setSiteType,
			fieldName: 'siteType',
			dispatch,
		});
	}, [dispatch, selectedSiteType, siteType]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedTechnology,
			currentValue: technology,
			setAction: setTechnology,
			fieldName: 'technology',
			dispatch,
		});
	}, [dispatch, selectedTechnology, technology]);

	useEffect(() => {
		setFilterUtil({
			selectedValue: selectedInstalledOnAsset,
			currentValue: installedOnAsset,
			setAction: setInstalledOnAsset,
			fieldName: 'installedOnAsset',
			dispatch,
		});
	}, [dispatch, installedOnAsset, selectedInstalledOnAsset]);

	return (
		<div className={styles.manageSiteContainer}>
			<Modal
				isConfirmDialogNeededOnClose
				isDirtyForm={isDirtyFormModal}
				isOpen={isSiteFormModal}
				onClose={closeSiteModal}
				title={editableSite ? 'Edit site' : 'Add site'}
				width={MODAL_WIDTH_580}
			>
				<ManageSiteForm
					editableSite={editableSite}
					generateSiteId={generateSiteId}
					isLoadingForm={isLoadingForm}
					setIsDirtyFormModal={setIsDirtyFormModal}
					submitHandler={submitHandler}
					toggleSiteModal={toggleSiteModal}
				/>
			</Modal>

			<Modal
				isOpen={isInRangeInform}
				onClose={closeInRangeInform}
				title='Information'
			>
				<Inform
					informText={
						isSingleSiteVerified
							? 'The Site is located within the linked Asset radius'
							: 'Sites are located within the linked Assets radius'
					}
					isLoading={inRadiusStatusesLoading}
					onClose={closeInRangeInform}
				/>
			</Modal>

			<Modal
				isOpen={isLinkFormModal}
				onClose={closeLinkModal}
				title='Link Asset'
				width={MODAL_WIDTH_580}
			>
				<LinkSiteToAssetForm
					closeModal={closeLinkModal}
					isLinkAccess={isLinkAccess}
					submitHandler={linkHandler}
				/>
			</Modal>

			<Modal
				isOpen={isRewriteCoordConfirm}
				onClose={closeUpdateSiteModal}
			>
				<Confirm
					negativeBtnText='Cancel'
					onSubmit={rewriteHandler}
					positiveBtnText='Update coordinates'
					question={
						!isSingleSiteVerified && rangeStatuses ? (
							<div className={styles.radiusInfoWrapper}>
								<span>Sites out of the Asset radius: {rangeStatuses.outOfRadius.length}</span>
								<span>Sites within the Asset radius: {rangeStatuses.inRadius.length}</span>
							</div>
						) : isSingleSiteVerified
							? siteOutOfRadiusText
							: siteCantBeLinkedWarning
					}
				>
					{isSingleSiteVerified ? updateCoordsText : updateCoordsAndLinkText}
				</Confirm>
			</Modal>

			<Modal
				isOpen={isUnlinkConfirm}
				onClose={closeUnlinkConfirm}
			>
				<Confirm
					negativeBtnText='Cancel'
					onSubmit={handleUnlinkItems}
					positiveBtnText='Unlink'
					question={
						singleSelectedSiteId
							? 'Are you sure you want to unlink this site from the Asset?'
							: 'Are you sure you want to unlink Sites from the Assets?'
					}
				>
					{/* eslint-disable-next-line max-len */}
					Unlinking doesn&apos;t delete the Site from database. It will only disconnect the Site from the Asset.
				</Confirm>
			</Modal>

			<Modal
				isOpen={isDeleteConfirm}
				onClose={closeDeleteConfirm}
			>
				<Confirm
					isLoading={isLoadingDeleteSites}
					onSubmit={handleDeleteItems}
					question='Are you sure you want to delete the Site(s)?'
				>
					Telecom Site(s) that are linked to Flex-5G units will be skipped.
				</Confirm>
			</Modal>

			{selectedVendorImport && isImportSites
				&& <ImportMapDataContainer
					acceptedFileTypes={acceptedSitesFileTypes}
					additionalParams={{ fieldsTechnology }}
					importType={EImportingDataType.site}
					importVendor={selectedVendorImport.value as string}
					isShowImportModal={isImportSites}
					toggleImportModal={toggleImportSites}
				/>
			}
			<PageTitle
				icon={Icons.manageSiteDBInverse()}
				title={EAdminPanelPageTitles.ManageSitesDB}
			/>
			<Modal
				isOpen={isImportFormModal}
				onClose={closeImportFormModal}
				title='Import sites'
				width={MODAL_WIDTH_580}
			>
				<SitesImportForm
					onClose={closeImportFormModal}
					onSubmit={toggleImport}
					vendorsOptions={vendorsOptions}
				/>
			</Modal>

			<div className={styles.siteListTableContainer}>
				<Table
					addTooltipText={addTooltipText}
					columns={columns}
					data={sitesDB}
					filters={filters}
					handleCustomDelete={handleCustomDelete}
					handleImport={openImportFormModal}
					isCreatable
					isEditAccess={isEditAccess}
					isImportable
					isLink
					isLinkAccess={isLinkAccess}
					isLoading={isLoadingSites}
					isOpenFilters={!!(vendor || siteType || technology || installedOnAsset)}
					isOpenSearch={!!siteId.length}
					isPinColumns
					isVerifyCoordinates
					pagination={pagination}
					search={search}
					searchThreshold={0}
					setCheckedIds={setCheckedIds}
					sort={sort}
					stickyLeftColumnIndexes={networkOperatorTelecomSitesViewer ? [0] : [0, 1]}
					toggleAddModal={openAddSiteModal}
					toggleUnlinkModal={toggleUnlinkConfirm}
					verifyCoordinates={verifyCoordinates}
				/>
			</div>
		</div>
	);
};

export default ManageSites;
